import { useMemo, useCallback } from 'react';
import { useStore } from '../store-hybrid';
import { Car } from '../types';

export interface RentalNotification {
  id: string;
  type: 'overdue' | 'due-very-soon' | 'due-soon' | 'due-today';
  car: Car;
  message: string;
  timeRemaining: string;
  urgency: 'critical' | 'high' | 'medium' | 'low';
  hoursLeft: number;
}

export const useNotifications = () => {
  const { cars } = useStore();

  // Helper function to format time remaining - using useCallback for stable reference
  const formatTimeRemaining = useCallback((ms: number, isOverdue: boolean) => {
    const absMs = Math.abs(ms);
    const days = Math.floor(absMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((absMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((absMs % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return `${days}d ${hours}h ${isOverdue ? 'overdue' : 'remaining'}`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m ${isOverdue ? 'overdue' : 'remaining'}`;
    } else {
      return `${minutes}m ${isOverdue ? 'overdue' : 'remaining'}`;
    }
  }, []);

  const rentalNotifications = useMemo(() => {
    const notifications: RentalNotification[] = [];
    
    // Debug: Check what cars we have (removed for production)
    
    cars.forEach(car => {
      if (!car.isRented || !car.rentedUntil) return;
      
      const now = new Date();
      
      // Handle Firestore Timestamp objects
      let rentedUntil: Date;
      if (car.rentedUntil && typeof car.rentedUntil === 'object' && 'seconds' in car.rentedUntil) {
        // Firestore Timestamp object
        rentedUntil = new Date((car.rentedUntil as any).seconds * 1000);
      } else {
        // Regular Date or date string
        rentedUntil = new Date(car.rentedUntil);
      }
      
      // Validate the converted date
      if (isNaN(rentedUntil.getTime())) {
        console.warn('Invalid rental date for car:', car.id);
        return;
      }
      
      const msUntilReturn = rentedUntil.getTime() - now.getTime();
      const hoursUntilReturn = msUntilReturn / (1000 * 60 * 60);
      const minutesUntilReturn = msUntilReturn / (1000 * 60);
      
      let notification: RentalNotification | null = null;
      
      if (hoursUntilReturn < 0) {
        notification = {
          id: `rental-${car.id}`,
          type: 'overdue',
          car,
          message: `Rental is ${Math.floor(Math.abs(hoursUntilReturn))} hours overdue`,
          timeRemaining: formatTimeRemaining(msUntilReturn, true),
          urgency: 'critical',
          hoursLeft: hoursUntilReturn
        };
      } else if (hoursUntilReturn <= 1) {
        notification = {
          id: `rental-${car.id}`,
          type: 'due-very-soon',
          car,
          message: `Rental due in ${Math.floor(minutesUntilReturn)} minutes`,
          timeRemaining: formatTimeRemaining(msUntilReturn, false),
          urgency: 'critical',
          hoursLeft: hoursUntilReturn
        };
      } else if (hoursUntilReturn <= 6) {
        notification = {
          id: `rental-${car.id}`,
          type: 'due-soon',
          car,
          message: `Rental due in ${Math.floor(hoursUntilReturn)} hours`,
          timeRemaining: formatTimeRemaining(msUntilReturn, false),
          urgency: 'high',
          hoursLeft: hoursUntilReturn
        };
      } else if (hoursUntilReturn <= 24) {
        notification = {
          id: `rental-${car.id}`,
          type: 'due-today',
          car,
          message: `Rental due today in ${Math.floor(hoursUntilReturn)} hours`,
          timeRemaining: formatTimeRemaining(msUntilReturn, false),
          urgency: 'medium',
          hoursLeft: hoursUntilReturn
        };
      }
      
      if (notification) {
        notifications.push(notification);
      }
    });
    
    // Debug: Log generated notifications (removed for production)
    
    // Sort by urgency and time remaining
    return notifications.sort((a, b) => {
      const urgencyOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      const urgencyDiff = urgencyOrder[a.urgency] - urgencyOrder[b.urgency];
      if (urgencyDiff !== 0) return urgencyDiff;
      return a.hoursLeft - b.hoursLeft; // Most urgent (least time) first
    });
  }, [cars, formatTimeRemaining]);

  const stats = useMemo(() => {
    const overdue = rentalNotifications.filter(n => n.type === 'overdue').length;
    const dueVerySoon = rentalNotifications.filter(n => n.type === 'due-very-soon').length;
    const dueSoon = rentalNotifications.filter(n => n.type === 'due-soon').length;
    const dueToday = rentalNotifications.filter(n => n.type === 'due-today').length;
    const total = rentalNotifications.length;
    const critical = rentalNotifications.filter(n => n.urgency === 'critical').length;
    
    return {
      total,
      overdue,
      dueVerySoon,
      dueSoon,
      dueToday,
      critical,
      hasUrgent: critical > 0
    };
  }, [rentalNotifications]);

  return {
    rentalNotifications,
    stats,
    formatTimeRemaining
  };
};
